import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Link, useParams } from "react-router-dom";
import { Eye } from "lucide-react";
import { axiosInstance } from "@/pages/auth/auth.context";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { Loading } from "@/components/ui/loading";

interface AdminEventSummaryDTO {
  id: string;
  companyName: string;
  address: string;
  startTime: string;
  endTime: string;
  totalPositions: number;
  filledPositions: number;
}

export default function ClientEvents() {
  const { id: clientId } = useParams();
  const { toast } = useToast();
  const [events, setEvents] = useState<AdminEventSummaryDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!clientId) return;

    const fetchClientEvents = async () => {
      setLoading(true);
      try {
        const response = await axiosInstance.get<AdminEventSummaryDTO[]>(
          `client/${clientId}/history`,
        );
        setEvents(response.data);
      } catch (error) {
        console.error("Error fetching client events:", error);
        setError("Failed to load client events");
        toast({
          title: "Error",
          description: "Failed to load client events",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchClientEvents();
  }, [clientId, toast]);

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), "MMM d, yyyy");
    } catch {
      return "Date unavailable";
    }
  };

  const getEventStatus = (filledPositions: number, totalPositions: number) => {
    if (filledPositions === 0) return "No Staff";
    if (filledPositions === totalPositions) return "Fully Staffed";
    return "Partially Staffed";
  };

  if (loading) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-foreground">Client Events</h1>
        </div>
        <div className="h-40 flex items-center justify-center">
          <Loading text="Loading client events..." />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-foreground">Client Events</h1>
        </div>
        <div className="h-40 flex items-center justify-center text-destructive">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-foreground">Client Events</h1>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Event Date</TableHead>
              <TableHead>Company</TableHead>
              <TableHead>Location</TableHead>
              <TableHead>Staff Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {events.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={5}
                  className="text-center text-muted-foreground"
                >
                  No events found for this client
                </TableCell>
              </TableRow>
            ) : (
              events.map((event) => (
                <TableRow key={event.id}>
                  <TableCell>{formatDate(event.startTime)}</TableCell>
                  <TableCell>{event.companyName}</TableCell>
                  <TableCell>{event.address}</TableCell>
                  <TableCell>
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        getEventStatus(
                          event.filledPositions,
                          event.totalPositions,
                        ) === "Fully Staffed"
                          ? "bg-success/10 text-success"
                          : getEventStatus(
                                event.filledPositions,
                                event.totalPositions,
                              ) === "Partially Staffed"
                            ? "bg-warning/10 text-warning"
                            : "bg-destructive/10 text-destructive"
                      }`}
                    >
                      {getEventStatus(
                        event.filledPositions,
                        event.totalPositions,
                      )}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <Link to={`/admin/events/${event.id}`}>
                      <Button variant="outline" size="sm">
                        <Eye className="h-4 w-4 mr-1" />
                        View Event
                      </Button>
                    </Link>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
